'use client';
import { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface CounterProps {
  to: number;
  from?: number;
  /** Animation duration in seconds. */
  duration?: number;
  /** Format the number on each frame (e.g. `n => n.toLocaleString('fr-FR')`). */
  format?: (n: number) => string;
  suffix?: string;
  prefix?: string;
  className?: string;
}

/**
 * Count-up number. Triggers when scrolled into view; respects reduced motion.
 * Uses ease-out so the value approaches the target gracefully.
 */
export function Counter({
  to,
  from = 0,
  duration = 1.8,
  format = (n) => Math.round(n).toLocaleString('fr-FR'),
  suffix,
  prefix,
  className,
}: CounterProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' });
  const reduced = useReducedMotion();
  const [value, setValue] = useState(reduced ? to : from);

  useEffect(() => {
    if (!inView || reduced) return;
    let raf = 0;
    const start = performance.now();
    const delta = to - from;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(from + delta * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, from, to, duration, reduced]);

  return (
    <span ref={ref} className={cn('tabular-nums', className)}>
      {prefix}
      {format(value)}
      {suffix}
    </span>
  );
}
